import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Plus } from 'lucide-react';
import { auditApi } from '@/api/audit';
import { unwrapError } from '@/api/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface WidgetAuditCardProps {
  allowedOrigins: string[];
  onAddOrigin: (origin: string) => void;
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export function WidgetAuditCard({ allowedOrigins, onAddOrigin }: WidgetAuditCardProps): JSX.Element {
  const since = React.useMemo(
    () => new Date(Date.now() - SEVEN_DAYS_MS).toISOString(),
    [],
  );

  const query = useQuery({
    queryKey: ['widget', 'audit', 'by-origin', since],
    queryFn: () => auditApi.byOrigin({ since }),
  });

  if (query.isLoading) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Chargement…
        </CardContent>
      </Card>
    );
  }

  if (query.error) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-destructive">{unwrapError(query.error)}</CardContent>
      </Card>
    );
  }

  const items = query.data?.items ?? [];
  const max = items.reduce((m, i) => Math.max(m, i.count), 1);
  const known = (origin: string): boolean => isOriginAllowed(origin, allowedOrigins);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Appels widget par origine — 7 derniers jours</CardTitle>
        <p className="mt-1 text-xs text-muted-foreground">
          Domaines qui ont effectué des appels à <code>/v1/chat/*</code>. Vert : origine
          autorisée. Rouge : origine inconnue (les requêtes ont été rejetées en 403 mais
          tracées en audit).
        </p>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            Aucun appel widget enregistré sur la période.
          </div>
        ) : (
          <ul className="space-y-2">
            {items.map((row) => {
              const allowed = known(row.origin);
              const width = Math.max(4, Math.round((row.count / max) * 100));
              return (
                <li key={row.origin} className="grid grid-cols-[1fr_auto] items-center gap-3">
                  <div>
                    <div className="flex items-center gap-2 font-mono text-xs">
                      {!allowed ? (
                        <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                      ) : null}
                      <span className={allowed ? '' : 'text-red-700'}>{row.origin}</span>
                      <span className="text-muted-foreground">· {row.count} appels</span>
                    </div>
                    <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className={`h-full ${allowed ? 'bg-emerald-500' : 'bg-red-500'}`}
                        style={{ width: `${width}%` }}
                      />
                    </div>
                  </div>
                  {!allowed ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      onClick={() => onAddOrigin(row.origin)}
                      title="Pre-fill the allowed origins field with this entry"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function isOriginAllowed(origin: string, allowed: readonly string[]): boolean {
  for (const entry of allowed) {
    if (entry === origin) return true;
    if (entry.includes('*.')) {
      const re = wildcardToRegex(entry);
      if (re.test(origin)) return true;
    }
  }
  return false;
}

function wildcardToRegex(pattern: string): RegExp {
  const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace('\\*', '[^.\\/]+');
  return new RegExp(`^${escaped}$`);
}
